import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MissingChannelsAlert } from "@/components/MissingChannelsAlert";
import { formatDuration, formatTime, STATUS_META } from "@/lib/format";
import { useRealtime } from "@/lib/realtime";
import { monthLabel, formatIDR } from "@/lib/month";
import {
  Activity, Coffee, Clock, MapPin, ListChecks, Palmtree, Check, X, Loader2,
  ArrowRight, Target, CalendarRange, FileText, CalendarClock, KeyRound, Settings,
  LayoutDashboard, TrendingUp, Handshake, Building2, ShieldCheck, Sparkles,
} from "lucide-react";

const LEAVE_BADGE = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
  rejected: "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
};

function LeaveChip({ status }) {
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${LEAVE_BADGE[status] || LEAVE_BADGE.pending}`}
      data-testid="leave-status-chip"
    >
      {status}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return "-";
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

const ADMIN_FEATURES = [
  { to: "/admin", label: "Monitoring", desc: "Live attendance", icon: Activity, tone: "from-emerald-500/20 to-emerald-500/5 text-emerald-400" },
  { to: "/admin/tasks", label: "Tasks", desc: "Kanban & proof", icon: ListChecks, tone: "from-sky-500/20 to-sky-500/5 text-sky-400" },
  { to: "/admin/performance", label: "Performance", desc: "Revenue & bonus", icon: Target, tone: "from-amber-500/20 to-amber-500/5 text-amber-400" },
  { to: "/admin/partners", label: "Partners", desc: "Contracts & signing", icon: Handshake, tone: "from-indigo-500/20 to-indigo-500/5 text-indigo-400" },
  { to: "/admin/partner-properties", label: "Properties", desc: "Live registry", icon: Building2, tone: "from-orange-500/20 to-orange-500/5 text-orange-400" },
  { to: "/admin/channels", label: "Channels", desc: "OTA vault", icon: KeyRound, tone: "from-yellow-500/20 to-yellow-500/5 text-yellow-400" },
  { to: "/admin/schedule", label: "Schedule", desc: "Shift planner", icon: CalendarClock, tone: "from-teal-500/20 to-teal-500/5 text-teal-400" },
  { to: "/admin/leave", label: "Leave", desc: "Approve requests", icon: Palmtree, tone: "from-emerald-600/20 to-emerald-600/5 text-emerald-300" },
  { to: "/admin/yearly", label: "Yearly", desc: "12-month matrix", icon: CalendarRange, tone: "from-violet-500/20 to-violet-500/5 text-violet-400" },
  { to: "/admin/reports", label: "Reports", desc: "Work reports", icon: FileText, tone: "from-rose-500/20 to-rose-500/5 text-rose-400" },
  { to: "/admin/settings", label: "Settings", desc: "Access & audit", icon: Settings, tone: "from-slate-400/20 to-slate-400/5 text-slate-300" },
];

const STAFF_FEATURES = [
  { to: "/staff", label: "Attendance", desc: "Check in / out", icon: Clock, tone: "from-emerald-500/15 to-emerald-500/5 text-emerald-600" },
  { to: "/staff/tasks", label: "Tasks", desc: "My checklist", icon: ListChecks, tone: "from-sky-500/15 to-sky-500/5 text-sky-600" },
  { to: "/staff/channels", label: "Channels", desc: "OTA credentials", icon: KeyRound, tone: "from-indigo-500/15 to-indigo-500/5 text-indigo-600" },
  { to: "/staff/schedule", label: "Schedule", desc: "My weekly shifts", icon: CalendarClock, tone: "from-teal-500/15 to-teal-500/5 text-teal-600" },
  { to: "/staff/performance", label: "Performance", desc: "My properties", icon: Target, tone: "from-amber-500/15 to-amber-500/5 text-amber-600" },
  { to: "/staff/leave", label: "Leave", desc: "Request time off", icon: Palmtree, tone: "from-rose-500/15 to-rose-500/5 text-rose-600" },
  { to: "/staff/reports", label: "Reports", desc: "Submit report", icon: FileText, tone: "from-orange-500/15 to-orange-500/5 text-orange-600" },
  { to: "/staff/yearly", label: "Yearly", desc: "12-month view", icon: CalendarRange, tone: "from-violet-500/15 to-violet-500/5 text-violet-600" },
];

const stagger = { show: { transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } };

function FeatureGrid({ features, dark }) {
  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="show"
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
      data-testid="quick-access-grid"
    >
      {features.map((f) => (
        <motion.div key={f.to} variants={item} whileHover={{ y: -3 }} transition={{ type: "spring", stiffness: 350 }}>
          <Link
            to={f.to}
            data-testid={`quick-${f.label.toLowerCase()}`}
            className={`group block rounded-2xl border p-3.5 h-full transition-all ${
              dark
                ? "border-slate-800 bg-slate-900/90 hover:border-indigo-500/50 hover:bg-slate-800/60"
                : "border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md"
            }`}
          >
            <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${f.tone} flex items-center justify-center mb-2.5 shadow-sm`}>
              <f.icon className="h-5 w-5" />
            </div>
            <div className={`font-semibold text-sm ${dark ? "text-white" : "text-slate-900"}`}>{f.label}</div>
            <div className={`text-[11px] ${dark ? "text-slate-400" : "text-slate-500"} truncate`}>{f.desc}</div>
          </Link>
        </motion.div>
      ))}
    </motion.div>
  );
}

function Kpi({ icon: Icon, label, value, tone, subtext, testid }) {
  return (
    <motion.div
      variants={item}
      className="rounded-2xl border border-slate-800 bg-slate-900/90 p-4 relative overflow-hidden shadow-sm hover:border-slate-700 transition-colors"
      data-testid={testid}
    >
      <div className={`absolute -right-4 -top-4 h-20 w-20 rounded-full bg-gradient-to-br ${tone} blur-xl opacity-35`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">{label}</span>
          <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${tone} flex items-center justify-center`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <div className="font-display text-3xl font-bold text-white tracking-tight">{value}</div>
        {subtext && <div className="text-[11px] text-slate-400 mt-1">{subtext}</div>}
      </div>
    </motion.div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dark = isAdmin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard/summary");
      setData(res.data);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useRealtime(
    useCallback(
      (msg) => {
        if (!msg?.event) return;
        if (isAdmin && msg.event === "attendance.check") toast(`${msg.user_name} — ${String(msg.type || "").replace("_", " ")}`);
        if (isAdmin && msg.event === "leave.created") toast(`New leave request from ${msg.user_name}`);
        if (!isAdmin && msg.event === "leave.reviewed" && msg.user_id === user?.id) toast(`Your leave request was ${msg.status}`);
        load();
      },
      [load, isAdmin, user?.id]
    )
  );

  const review = async (id, status) => {
    try {
      await api.patch(`/leaves/${id}/review`, { status });
      toast.success(`Leave ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading || !data) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${dark ? "bg-[#14110F]" : "bg-[#FAF7F2]"}`}>
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const perf = data.performance;
  const barPct = Math.max(0, Math.min(perf?.pct || 0, 100));
  const sub = dark ? "text-slate-400" : "text-slate-500";
  const card = dark ? "border-slate-800 bg-slate-900/90" : "border-slate-200 bg-white";
  const hello = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className={`min-h-screen ${dark ? "bg-[#14110F] dark-scroll text-slate-100" : "bg-[#FAF7F2] text-slate-900"}`}>
      <AppNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-7" data-testid="dashboard-page">
        {/* Hero Banner with Sustainable Earth Gradient */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl p-7 sm:p-9 bg-gradient-to-br from-[#C26842] via-[#A9522F] to-[#1F1B16] text-white shadow-lg border border-amber-900/40"
          data-testid="dashboard-hero"
        >
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage: "radial-gradient(circle at 20% 20%, white 1.5px, transparent 1.5px)",
              backgroundSize: "28px 28px",
            }}
          />
          <div className="relative flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/20 backdrop-blur-md rounded-full px-3 py-1 mb-3 text-amber-100 shadow-sm">
                <LayoutDashboard className="h-3.5 w-3.5" />
                {isAdmin ? "HR & Operations Command Center" : "Staff Workspace"}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="dashboard-greeting">
                {hello}, {user?.name?.split(" ")[0]} 👋
              </h1>
              <p className="text-sm text-amber-100/90 mt-1 flex items-center gap-2 flex-wrap">
                <span>{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
                <span>·</span>
                <span className="font-medium">BedsolvingOps (Bali)</span>
              </p>
            </div>

            <div className="flex items-center gap-3 bg-black/25 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-3 shadow-sm">
              <TrendingUp className="h-8 w-8 text-amber-300" />
              <div>
                <div className="text-2xl font-bold font-display leading-none text-white">{perf.pct}%</div>
                <div className="text-[11px] text-amber-100/80">{monthLabel(perf.month)} yield achieved</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick access to all modules */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className={`text-xs font-bold uppercase tracking-wider ${sub} flex items-center gap-1.5`}>
              <Sparkles className="h-3.5 w-3.5 text-indigo-400" /> Quick Access
            </h2>
          </div>
          <FeatureGrid features={isAdmin ? ADMIN_FEATURES : STAFF_FEATURES} dark={dark} />
        </div>

        {isAdmin ? (
          <>
            {/* Live Operational KPIs */}
            <motion.div variants={stagger} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
              <Kpi icon={Activity} label="Working now" value={data.attendance.working} tone="from-emerald-500/40 to-emerald-500/10 text-emerald-400" subtext={`${data.attendance.total_staff} staff total`} testid="kpi-working" />
              <Kpi icon={Coffee} label="On break" value={data.attendance.on_break} tone="from-amber-500/40 to-amber-500/10 text-amber-400" subtext="active breaks" testid="kpi-break" />
              <Kpi icon={Clock} label="Late today" value={data.attendance.late_today} tone="from-rose-500/40 to-rose-500/10 text-rose-400" subtext="post-shift check-ins" testid="kpi-late" />
              <Kpi icon={MapPin} label="Out of zone" value={data.attendance.out_of_zone_today} tone="from-orange-500/40 to-orange-500/10 text-orange-400" subtext="GPS distance alert" testid="kpi-outzone" />
              <Kpi icon={Palmtree} label="Pending leave" value={data.pending_leaves_count} tone="from-teal-500/40 to-teal-500/10 text-teal-400" subtext="need approval" testid="kpi-pending-leave" />
            </motion.div>

            <MissingChannelsAlert dark />

            {/* Main Operational Overview */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Financial & Yield Performance Card */}
              <div className={`lg:col-span-2 rounded-2xl border p-5 ${card} shadow-sm`} data-testid="dash-performance-card">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h3 className="font-display font-semibold text-lg text-white flex items-center gap-2">
                      <Target className="h-5 w-5 text-indigo-400" />
                      Performance &amp; Yield — {monthLabel(perf.month)}
                    </h3>
                    <p className={`text-xs ${sub}`}>Across {data.properties_count || "all"} live properties · target vs realized</p>
                  </div>
                  <Link to="/admin/performance">
                    <Button size="sm" variant="outline" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="dash-goto-performance">
                      Performance grid <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </div>

                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className={sub}>Monthly target progress</span>
                  <span className="font-mono font-semibold text-amber-400" data-testid="dash-perf-pct">{perf.pct}%</span>
                </div>
                <div className="h-2.5 rounded-full overflow-hidden bg-slate-800/80">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#C26842] via-[#E8AD4A] to-[#7A8A5B]"
                    initial={{ width: 0 }}
                    animate={{ width: `${barPct}%` }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 mt-5">
                  <div className="rounded-xl bg-slate-800/40 p-3 border border-slate-800">
                    <div className={`text-[11px] ${sub}`}>Target (Revenue)</div>
                    <div className="font-mono font-semibold text-sm text-white mt-0.5">{formatIDR(perf.target)}</div>
                  </div>
                  <div className="rounded-xl bg-slate-800/40 p-3 border border-slate-800">
                    <div className={`text-[11px] ${sub}`}>Realized (Final)</div>
                    <div className="font-mono font-semibold text-sm text-white mt-0.5">{formatIDR(perf.final)}</div>
                  </div>
                  <div className="rounded-xl bg-slate-800/40 p-3 border border-slate-800">
                    <div className={`text-[11px] ${sub}`}>Net Profit</div>
                    <div className="font-mono font-semibold text-sm text-indigo-300 mt-0.5">{formatIDR(perf.net_profit)}</div>
                  </div>
                  <div className="rounded-xl bg-slate-800/40 p-3 border border-slate-800">
                    <div className={`text-[11px] ${sub}`}>Staff Bonus Pool</div>
                    <div className="font-mono font-semibold text-sm text-emerald-400 mt-0.5">{formatIDR(perf.bonus)}</div>
                  </div>
                </div>

                {/* Bottom summaries: Partners & Tasks */}
                <div className="mt-5 pt-4 border-t border-slate-800/80 grid sm:grid-cols-2 gap-3">
                  <Link
                    to="/admin/partners"
                    className="flex items-center justify-between p-2.5 rounded-xl bg-slate-800/30 hover:bg-slate-800/60 border border-slate-800 transition-colors"
                  >
                    <div className="flex items-center gap-2 text-xs">
                      <Handshake className="h-4 w-4 text-indigo-400" />
                      <span className="text-slate-300 font-medium">Partner Contracts</span>
                    </div>
                    <Badge variant="outline" className="border-indigo-500/40 text-indigo-300 text-[10px]">
                      {data.partners_pending ? `${data.partners_pending} pending sign` : `${data.partners_count || 0} total`}
                    </Badge>
                  </Link>
                  <Link
                    to="/admin/tasks"
                    className="flex items-center justify-between p-2.5 rounded-xl bg-slate-800/30 hover:bg-slate-800/60 border border-slate-800 transition-colors"
                  >
                    <div className="flex items-center gap-2 text-xs">
                      <ListChecks className="h-4 w-4 text-amber-400" />
                      <span className="text-slate-300 font-medium">Active Tasks</span>
                    </div>
                    <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]">
                      {data.open_tasks} open
                    </Badge>
                  </Link>
                </div>
              </div>

              {/* Pending Leaves Review Widget */}
              <div className={`rounded-2xl border p-5 ${card} shadow-sm flex flex-col`} data-testid="dash-pending-leaves">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-semibold text-lg text-white flex items-center gap-2">
                    <Palmtree className="h-5 w-5 text-teal-400" /> Leave Approvals
                  </h3>
                  <Link to="/admin/leave">
                    <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white" data-testid="dash-goto-leave">
                      All <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </div>

                {data.pending_leaves.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
                    <ShieldCheck className="h-10 w-10 text-emerald-500/50 mb-2" />
                    <p className="text-sm font-medium text-white">All caught up!</p>
                    <p className={`text-xs ${sub} mt-0.5`}>No pending leave requests awaiting approval.</p>
                  </div>
                ) : (
                  <div className="space-y-3 flex-1 overflow-y-auto">
                    {data.pending_leaves.map((l) => (
                      <div key={l.id} className="rounded-xl border border-slate-800/90 bg-slate-800/40 p-3" data-testid={`dash-leave-${l.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white">{l.user_name}</span>
                          <LeaveChip status={l.status} />
                        </div>
                        <div className={`text-xs mt-1 ${sub}`}>
                          <span className="capitalize font-medium text-slate-300">{l.type}</span> · {fmtDate(l.start_date)} – {fmtDate(l.end_date)}
                        </div>
                        <div className="flex gap-2 mt-2.5">
                          <Button
                            size="sm"
                            className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white flex-1 text-xs"
                            onClick={() => review(l.id, "approved")}
                            data-testid={`dash-approve-${l.id}`}
                          >
                            <Check className="h-3.5 w-3.5 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 flex-1 text-xs"
                            onClick={() => review(l.id, "rejected")}
                            data-testid={`dash-reject-${l.id}`}
                          >
                            <X className="h-3.5 w-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* Staff Workspace Cards */
          <div className="grid md:grid-cols-3 gap-6">
            {/* Status / Live Timer */}
            <div className={`rounded-2xl border p-5 ${card} shadow-sm`} data-testid="dash-status-card">
              {(() => {
                const st = STATUS_META[data.attendance.status] || STATUS_META.checked_out;
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Today's Shift</span>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`} data-testid="dash-status-badge">
                        {st.label}
                      </span>
                    </div>
                    <div className="mt-4">
                      <div className="text-xs text-slate-500 mb-1">Worked today</div>
                      <div className="font-mono text-3xl font-bold text-slate-900" data-testid="dash-worked">
                        {formatDuration(data.attendance.worked_seconds)}
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="h-3.5 w-3.5 text-indigo-500" />
                        Check-in: {formatTime(data.attendance.check_in_time)}
                      </div>
                    </div>
                    <Link to="/staff" className="block mt-5">
                      <Button className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="dash-goto-attendance">
                        Open Attendance <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </>
                );
              })()}
            </div>

            {/* My Properties & Performance */}
            <div className={`rounded-2xl border p-5 ${card} shadow-sm`} data-testid="dash-perf-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-lg text-slate-900 flex items-center gap-2">
                  <Target className="h-4 w-4 text-indigo-600" /> My Properties
                </h3>
                <Link to="/staff/performance">
                  <Button size="sm" variant="ghost" data-testid="dash-goto-performance">
                    Details <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </div>
              <p className="text-xs text-slate-500 mb-2">{monthLabel(perf.month)} achievement</p>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-slate-600 font-medium">
                  {formatIDR(perf.final)} / {formatIDR(perf.target)}
                </span>
                <span className="font-mono font-semibold text-slate-900" data-testid="dash-perf-pct">
                  {perf.pct}%
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#C26842] via-[#E8AD4A] to-[#7A8A5B]"
                  initial={{ width: 0 }}
                  animate={{ width: `${barPct}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-sm text-slate-600 flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-slate-400" /> {data.open_tasks} open tasks
                </span>
                <Link to="/staff/tasks">
                  <Button size="sm" variant="ghost" data-testid="dash-goto-tasks">
                    Tasks <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* My Leave Requests & Channels/Schedule */}
            <div className={`rounded-2xl border p-5 ${card} shadow-sm flex flex-col justify-between`} data-testid="dash-leave-card">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-semibold text-lg text-slate-900 flex items-center gap-2">
                    <Palmtree className="h-4 w-4 text-emerald-600" /> My Leave
                  </h3>
                  <Link to="/staff/leave">
                    <Button size="sm" variant="ghost" data-testid="dash-goto-leave">
                      Request <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </div>

                {data.leaves.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4 text-center">No leave requests this month.</p>
                ) : (
                  <div className="space-y-2">
                    {data.leaves.slice(0, 3).map((l) => (
                      <div key={l.id} className="flex items-center justify-between text-xs border border-slate-100 rounded-lg p-2" data-testid={`dash-leave-${l.id}`}>
                        <div>
                          <span className="font-medium capitalize text-slate-800">{l.type}</span>
                          <span className="text-slate-400 ml-1.5">{fmtDate(l.start_date)}</span>
                        </div>
                        <LeaveChip status={l.status} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-between mt-3">
                <Link to="/staff/channels" className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1">
                  <KeyRound className="h-3.5 w-3.5" /> Channels ({data.channels_count || 0})
                </Link>
                <Link to="/staff/schedule" className="text-xs font-medium text-teal-600 hover:underline flex items-center gap-1">
                  <CalendarClock className="h-3.5 w-3.5" /> Schedule
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
